import { useEffect, useState } from 'react';
import { CircularProgress } from '../CircularProgress';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { KPICell, SectionFrame, performanceColor } from '../scheduled/shared';
import {
  salesOrdersDashboardAPI,
  type SalesOrdersDashboardData,
  type SalesOrdersCurrentMonthData,
  type SalesOrdersDashboardParams,
} from '../../../lib/api/sales-orders-dashboard';
import type { OpenDrillDown } from './SalesOrdersShell';

const PALETTE = {
  onTime: '#0f766e',
  early:  '#2563eb',
  late:   '#dc2626',
  pending: '#cbd5e1',
};

/** Processing-stage donut: order, labels and colours (mirrors the grid Status). */
const STAGE_META: { key: string; label: string; color: string }[] = [
  { key: 'docked',    label: 'Docked',    color: '#3b82f6' },
  { key: 'wip',       label: 'WIP',       color: '#f59e0b' },
  { key: 'on_hold',   label: 'On Hold',   color: '#f97316' },
  { key: 'completed', label: 'Completed', color: '#10b981' },
];

/** Hard cap on how far back the stepper may page (mirrors backend clamp). */
const MAX_MONTH_OFFSET = 24;

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Pretty label for a month-window start date string ('YYYY-MM-DD'). */
function monthLabel(windowStart: string): string {
  const d = new Date(`${windowStart}T00:00:00Z`);
  return `${MONTH_LABELS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

interface Props {
  data: SalesOrdersDashboardData;
  formatCurrency: (n: number) => string;
  formatDate: (date: string | Date, options?: { format?: string; includeTime?: boolean }) => string;
  openDrillDown: OpenDrillDown;
  /** Feature-flag gate — when false, OTIF tile/drill-down is not rendered. */
  showOtif: boolean;
  /** Supplier-portal install dispatches goods — relabel "Received" → "Delivered". */
  isSupplierPortalMode: boolean;
  /** Active supplier/customer/part filters — mirrored on the stepper refetch. */
  filterParams: SalesOrdersDashboardParams;
  /**
   * Customer (counterparty / customer-side) viewers see ONLY the top KPI cards —
   * the chart sections below are hidden. Supplier/admin viewers keep everything.
   */
  isCustomer: boolean;
}

/**
 * Current-calendar-month panel: Order Status + Delivery + Cost, mirroring the
 * Current-Week panel but windowed on the calendar month (1st → end-of-month,
 * capped at today for the current month). Prev/Next page whole months.
 *
 * The full dashboard payload does not carry the month block, so even month 0 is
 * fetched from the lightweight ``current-month`` stepper endpoint on mount.
 */
export function CurrentMonth({ data, formatCurrency, formatDate, openDrillDown, showOtif, isSupplierPortalMode, filterParams, isCustomer }: Props) {
  const deliveredLabel = isSupplierPortalMode ? 'Delivered' : 'Received';

  // offset 0 = this calendar month. Every offset (incl. 0) is fetched from the
  // stepper endpoint because the full payload has no month block.
  const [monthOffset, setMonthOffset] = useState(0);
  const [block, setBlock] = useState<SalesOrdersCurrentMonthData | null>(null);
  const [loading, setLoading] = useState(true);

  // Reset to "this month" whenever the underlying payload reloads (filters /
  // company change in the shell).
  useEffect(() => {
    setMonthOffset(0);
  }, [data]);

  // Serialise filters so the fetch effect only refires when they truly change.
  const filterKey = JSON.stringify(filterParams);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    salesOrdersDashboardAPI
      .getCurrentMonth(monthOffset, filterParams)
      .then((resp) => { if (!cancelled) setBlock(resp.data.current_month); })
      .catch(() => { if (!cancelled) setBlock(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // filterKey captures filterParams; monthOffset drives the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthOffset, filterKey]);

  const isThisMonth = monthOffset === 0;

  if (!block) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="ml-2 text-muted-foreground">Loading month…</span>
      </div>
    );
  }

  const cm = block;
  const period = {
    start: cm.window_start,
    end: cm.window_end,
    label: isThisMonth ? 'this month' : monthLabel(cm.window_start),
  };
  const monthRangeLabel = `${formatDate(cm.window_start)} – ${formatDate(cm.window_end)}`;

  const deliveriesByWeek = cm.deliveries_by_week.map((w) => ({
    label: w.label,
    weekStart: w.week_start,
    weekEnd: w.week_end,
    onTime: w.on_time,
    early: w.early,
    late: w.late,
    pending: w.pending,
  }));

  const otdSplit = [
    { name: 'On time', value: cm.delivery.on_time, color: PALETTE.onTime, status: 'on_time' as const },
    { name: 'Early',   value: cm.delivery.early,   color: PALETTE.early,  status: 'early' as const },
    { name: 'Late',    value: cm.delivery.late,    color: PALETTE.late,   status: 'late' as const },
  ];
  const otdEmpty = otdSplit.every((d) => d.value === 0);
  const otdData = otdEmpty
    ? [{ name: 'No data yet', value: 1, color: '#f1f5f9', status: null }]
    : otdSplit;

  const statusSplitRaw = cm.status_split || {};
  const statusSplit = STAGE_META
    .map((m) => ({ name: m.label, key: m.key, value: statusSplitRaw[m.key] || 0, color: m.color }))
    .filter((d) => d.value > 0);
  const statusEmpty = statusSplit.length === 0;
  const statusData = statusEmpty ? [{ name: 'No data', key: '', value: 1, color: '#f1f5f9' }] : statusSplit;
  const statusTotal = statusSplit.reduce((acc, d) => acc + d.value, 0);
  const wipCount = cm.order_status.wip_count ?? 0;
  const onHoldCount = cm.order_status.on_hold_count ?? 0;

  const handleWeekBarClick = (
    weekLabel: string,
    weekStart: string,
    weekEnd: string,
    statusKey: 'on_time' | 'early' | 'late' | 'pending',
  ) => {
    const weekPeriod = { start: weekStart, end: weekEnd, label: weekLabel };
    if (statusKey === 'pending') {
      openDrillDown({
        scope: 'outstanding',
        period: weekPeriod,
        title: `Pending deliveries — ${weekLabel}`,
        subtitle: 'Live lines due that week still outstanding',
      });
      return;
    }
    openDrillDown({
      scope: 'received',
      period: weekPeriod,
      title: `${statusKey === 'on_time' ? 'On-time' : statusKey === 'early' ? 'Early' : 'Late'} deliveries — ${weekLabel}`,
      subtitle: 'Dispatches that week',
      extraFilters: { on_time_status: statusKey },
    });
  };

  const handleOtdSliceClick = (status: 'on_time' | 'early' | 'late' | null) => {
    if (!status || otdEmpty) return;
    openDrillDown({
      scope: 'otd',
      period,
      title: `${status === 'on_time' ? 'On-time' : status === 'early' ? 'Early' : 'Late'} — lines due this month`,
      subtitle: 'Month to date • OTD outcome of lines due',
      extraFilters: { on_time_status: status },
    });
  };

  const handleStatusSliceClick = (key?: string, label?: string) => {
    if (statusEmpty || !key) return;
    const scope = ({ wip: 'wip', on_hold: 'on_hold', docked: 'docked', completed: 'completed' } as const)[
      key as 'wip' | 'on_hold' | 'docked' | 'completed'
    ];
    if (!scope) return;
    openDrillDown({
      scope,
      period,
      title: `${label} — ${period.label}`,
      subtitle: 'Active-stage lines due this month',
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-4 py-2.5">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMonthOffset((o) => Math.min(o + 1, MAX_MONTH_OFFSET))}
            disabled={monthOffset >= MAX_MONTH_OFFSET || loading}
            aria-label="Previous month"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="min-w-[180px] text-center">
            <div className="text-sm font-semibold text-slate-900">
              {isThisMonth ? 'This Month' : monthLabel(cm.window_start)}
            </div>
            <div className="text-[11px] text-slate-500 tabular-nums">{monthRangeLabel}</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setMonthOffset((o) => Math.max(o - 1, 0))}
            disabled={isThisMonth || loading}
            aria-label="Next month"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        {loading && (
          <span className="flex items-center gap-1.5 text-xs text-slate-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading month…
          </span>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        <SectionFrame title="Order Status" accent="red" subtitle={isThisMonth ? 'due this month' : 'due in month'}>
          <div className="grid grid-cols-2 gap-3">
            <KPICell
              label="WIP"
              value={<span className="text-2xl font-semibold tabular-nums">{wipCount.toLocaleString()}</span>}
              foot="lines due this month with Status WIP"
              onClick={() => openDrillDown({
                scope: 'wip',
                period,
                title: 'WIP — This Month',
                subtitle: 'Lines due this month — Status WIP',
              })}
            />
            <KPICell
              label="On Hold"
              value={<span className="text-2xl font-semibold tabular-nums">{onHoldCount.toLocaleString()}</span>}
              foot="lines due this month on hold"
              onClick={() => openDrillDown({
                scope: 'on_hold',
                period,
                title: 'On Hold — This Month',
                subtitle: 'Lines due this month that are held',
              })}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Delivery" accent="green" subtitle={isThisMonth ? '1st → today' : 'full month'}>
          <div className={`grid ${showOtif ? 'grid-cols-2' : 'grid-cols-1'} gap-3 items-start`}>
            {showOtif && (
              <KPICell
                label="OTIF"
                value={<CircularProgress percentage={cm.delivery.otif_pct} size={68} strokeWidth={6} color={performanceColor(cm.delivery.otif_pct)} />}
                foot={`${cm.delivery.otif_met}/${cm.delivery.total_due} order lines`}
                onClick={() => openDrillDown({
                  scope: 'otif_miss',
                  period,
                  title: 'OTIF Misses — This Month',
                  subtitle: 'Past-due lines that missed on-time-in-full',
                })}
              />
            )}
            <KPICell
              label="OTD"
              value={<CircularProgress percentage={cm.delivery.otd_pct} size={68} strokeWidth={6} color={performanceColor(cm.delivery.otd_pct)} />}
              foot={`${cm.delivery.otd_met}/${cm.delivery.total_due} on time`}
              onClick={() => openDrillDown({
                scope: 'late',
                period,
                title: 'OTD Misses — This Month',
                subtitle: 'Past-due lines that missed on-time delivery',
              })}
            />
          </div>
        </SectionFrame>

        <SectionFrame title="Cost" accent="blue" subtitle="open book now · delivered this month">
          <div className="grid grid-cols-3 gap-3">
            <KPICell
              label="Open Order Lines"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(data.portfolio.open_orders_value)}</span>}
              foot={`${(data.portfolio.open_lines ?? 0).toLocaleString()} open lines (not yet late)`}
              onClick={() => openDrillDown({
                scope: 'open',
                period: null,
                title: 'Open Order Lines',
                subtitle: 'Live lines still open and not yet late',
              })}
            />
            <KPICell
              label={deliveredLabel}
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(cm.cost.received_value)}</span>}
              foot={`of ${formatCurrency(cm.cost.total_value)} committed`}
              onClick={() => openDrillDown({
                scope: 'delivered',
                period,
                title: `${deliveredLabel} Value — This Month`,
                subtitle: 'Lines due this month that have been received',
              })}
            />
            <KPICell
              label="Arrears"
              value={<span className="text-sm font-semibold leading-tight tabular-nums">{formatCurrency(data.portfolio.arrears_value)}</span>}
              foot={`${(data.portfolio.arrears_lines ?? 0).toLocaleString()} overdue lines${data.portfolio.arrears_value === 0 && (data.portfolio.arrears_lines ?? 0) > 0 ? ' • unpriced in source' : ''}`}
              onClick={() => openDrillDown({
                scope: 'arrears',
                period: null,
                title: 'Arrears',
                subtitle: 'All overdue lines still open',
              })}
            />
          </div>
        </SectionFrame>
      </div>

      {/* Customer (counterparty) viewers see ONLY the top KPI cards above — the
          chart sections below are supplier/admin-only. */}
      {!isCustomer && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">Deliveries by Week</CardTitle>
              <p className="text-[11px] text-muted-foreground">By week • {isThisMonth ? 'this month' : monthRangeLabel}</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={deliveriesByWeek} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                    <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={{ stroke: '#cbd5e1' }} tickLine={false} interval={0} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                    <Bar dataKey="onTime"  stackId="a" fill={PALETTE.onTime}  name="On time"
                      onClick={(d) => {
                        const p = d?.payload as { label?: string; weekStart?: string; weekEnd?: string } | undefined;
                        if (p?.label && p.weekStart && p.weekEnd) handleWeekBarClick(p.label, p.weekStart, p.weekEnd, 'on_time');
                      }}
                      style={{ cursor: 'pointer' }} />
                    <Bar dataKey="early"   stackId="a" fill={PALETTE.early}   name="Early"
                      onClick={(d) => {
                        const p = d?.payload as { label?: string; weekStart?: string; weekEnd?: string } | undefined;
                        if (p?.label && p.weekStart && p.weekEnd) handleWeekBarClick(p.label, p.weekStart, p.weekEnd, 'early');
                      }}
                      style={{ cursor: 'pointer' }} />
                    <Bar dataKey="late"    stackId="a" fill={PALETTE.late}    name="Late"
                      onClick={(d) => {
                        const p = d?.payload as { label?: string; weekStart?: string; weekEnd?: string } | undefined;
                        if (p?.label && p.weekStart && p.weekEnd) handleWeekBarClick(p.label, p.weekStart, p.weekEnd, 'late');
                      }}
                      style={{ cursor: 'pointer' }} />
                    <Bar dataKey="pending" stackId="a" fill={PALETTE.pending} name="Pending" radius={[3, 3, 0, 0]}
                      onClick={(d) => {
                        const p = d?.payload as { label?: string; weekStart?: string; weekEnd?: string } | undefined;
                        if (p?.label && p.weekStart && p.weekEnd) handleWeekBarClick(p.label, p.weekStart, p.weekEnd, 'pending');
                      }}
                      style={{ cursor: 'pointer' }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">OTD Mix</CardTitle>
              <p className="text-[11px] text-muted-foreground">Month to date • {cm.delivery.total_with_status.toLocaleString()} order lines due</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={otdData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={56}
                      outerRadius={84}
                      paddingAngle={otdEmpty ? 0 : 2}
                      stroke="none"
                      onClick={(d: { status?: 'on_time' | 'early' | 'late' | null }) => handleOtdSliceClick(d?.status ?? null)}
                      style={{ cursor: otdEmpty ? 'default' : 'pointer' }}
                    >
                      {otdData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">Status Split</CardTitle>
              <p className="text-[11px] text-muted-foreground">Active stages (excl. Shipped/Cancelled) • {statusTotal.toLocaleString()} lines</p>
            </CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={statusData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%" cy="50%"
                      innerRadius={56}
                      outerRadius={84}
                      paddingAngle={statusEmpty ? 0 : 2}
                      stroke="none"
                      onClick={(d: { key?: string; name?: string }) => handleStatusSliceClick(d?.key, d?.name)}
                      style={{ cursor: statusEmpty ? 'default' : 'pointer' }}
                    >
                      {statusData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
